/* eslint-disable jsx-a11y/anchor-is-valid, react/no-unsafe, react/sort-comp */
/**
 * Render a modal dialog.
 *
 * WARNING: this modal is deprecated, please use modal.jsx.
 *
 * Example:
 *
 *      <Modal onClose={this.props.onClose}>
 *          ... children ...
 *      </Modal>
 *
 */

import {css, StyleSheet, type CSSProperties} from "aphrodite";
import classNames from "classnames";
import * as React from "react";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    borderRadiusLarge,
    contentPadding,
    mediaQueries,
    minContainerWidth,
    zindexAboveModal,
    zindexModal,
    zindexModalBackdrop,
} from "@khan/shared-styles";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedIcon from "#shared-styles-package/deprecated-icon.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {baseLineHeight} from "#shared-styles-package/deprecated-typography.ts";

const modalDialogPadding = "15px";
const modalSectionPadding = "9px";

const closeIcon = `
M6.26353762,4.99851587 L9.73097464,1.53107884 C10.0836373,1.17841618
10.0842213,0.612127047 9.73530496,0.263210718 C9.38395604,-0.0881381913
8.81874474,-0.0837668714 8.46743686,0.267541014 L4.99999981,3.73497806
L1.5325628,0.267541051 C1.1812549,-0.0837668481 0.616043606,
-0.0881381955 0.264694717,0.263210694 C-0.0842215912,0.612127004
-0.0836375768,1.17841613 0.269025093,1.5310788 L3.73646206,4.9985158
L0.269025109,8.46595276 C-0.083637537,8.81861541 -0.0842215923,
9.38490462 0.264694642,9.73382106 C0.616043456,10.0851701 1.18125469,
10.0807988 1.53256259,9.72949093 L4.99999988,6.26205363 L8.46743739,
9.72949117 C8.8187453,10.0807991 9.38395655,10.0851704 9.73530537,
9.73382138 C10.0842216,9.38490498 10.0836375,8.81861579 9.73097488,
8.46595313 L6.26353762,4.99851587 Z`;

type Props = {
    // Should the modal have role="alertdialog" status"
    alert: boolean;
    // Label to provide as aria-label for close link ("X"). Note: this
    // should be descriptive if possible (e.g. "close sign up dialog")
    ariaCloseLabel: string;
    // Bootstrap modal's backdrop argument: Includes a modal-backdrop
    // element. Alternatively, specify "static" for a backdrop which doesn't
    // close the modal on click. Defaults to true.
    backdrop: boolean | string;
    // The Modal wraps all incoming nodes
    children?: React.ReactNode;
    // A class name to set on the modal dialog
    className: string;
    // A callback function called when the modal mounts to determine the
    // element that should receive focus
    elementToFocus?: () => HTMLElement;
    // An array containing extra aphrodite styles; these will override any
    // existing styles.
    extraStyles: [any];
    // Should the dialog be displayed extra wide (1000px)
    extraWide: boolean;
    // The node contents of the footer
    footer?: React.ReactNode;
    // Force the modal to be on top of most other elements, such as the
    // learn subject menu
    forceTop: boolean;
    // Display the modal full screen
    fullScreen: boolean;
    // Display the modal full screen, but only on mobile
    fullScreenOnMobile: boolean;
    // Display the modal (and backdrop) inline in the page, rather than
    // overlaying the entire page.
    inline: boolean;
    // Close the modal when esc is pressed? Defaults to true.
    keyboard: boolean;
    // A callback function that's called when the modal is closed by
    // the user (e.g. pressing escape, clicking the backdrop)
    onClose: () => unknown;
    // Whether padding should be automatically added around the children
    padded: boolean;
    // If true, prevents scrolling of the body when the modal is displayed
    preventBodyScroll: boolean;
    // If true, will prevent the modal from scrolling the window to the
    // top, preserving the scrolling position when opening the modal.
    preventScrollOnShow: boolean;
    // By default if we refocus the element cause the modal to be shown.
    // If this is set to false, we disable this behavior.
    // TODO(kevinb) remove after addressing https://app.asana.com/0/176167022854494/187833868411213
    // (see also /r/asana-links)
    refocusActiveElement: boolean;
    // Make the modal work better on small screens, by shrinking it to
    // always fit on the page and using overflow auto.
    responsive: boolean;
    // Whether the height of the modal expands to reach near the bottom
    // of the screen, or whether it will wrap the contents
    // Note: Only active when `responsive` is also true
    responsiveHeight: boolean;
    // Whether to restrict tab access to the modal body when the modal is
    // mounted (this is generally the expected and a11y-friendly behavior)
    restrictTabbing: boolean;
    // Whether the Modal component should handle hiding itself or not
    shouldHandleClose?: boolean;
    // Show a "X" close button
    showCloseButton: boolean;
    // Show an "X"  close button absolutely positioned at the top right
    // (default "X" will take up space)
    showCloseButtonAbsolute: boolean;
    // A title to display in a header
    title: React.ReactNode;
    // Should the dialog be displayed wide (700px)
    wide: boolean;
};

type State = {
    visible: boolean;
    activeElement: HTMLElement | null | undefined;
};

/**
 * @deprecated Please use Wonder Blocks Modal instead.
 */
class DeprecatedModal extends React.Component<Props, State> {
    static defaultProps: Props = {
        ariaCloseLabel: "Close",
        alert: false,
        backdrop: true,
        className: "",
        // @ts-expect-error - TS2322 - Type '[]' is not assignable to type '[any]'.
        extraStyles: [],
        extraWide: false,
        forceTop: false,
        fullScreen: false,
        fullScreenOnMobile: false,
        inline: false,
        keyboard: true,
        onClose: () => {},
        padded: true,
        preventBodyScroll: false,
        preventScrollOnShow: false,
        refocusActiveElement: true,
        responsive: false,
        responsiveHeight: true,
        restrictTabbing: true,
        showCloseButton: false,
        shouldHandleClose: true,
        showCloseButtonAbsolute: false,
        title: "",
        wide: false,
    };

    /**
     * Return a style mixin that would set the modal to the given width.
     * Mix it into your `extraStyles` Aphrodite classes, including in media
     * queries if appropriate.
     */
    static width(width: number): CSSProperties {
        return {
            margin: `0 0 0 ${-width / 2}px`,
            width: width,
            maxWidth: width,
        };
    }

    state: State = {
        visible: true,
        activeElement: null,
    };

    // TODO(jangmi, WEB-1514): Remove usage of `UNSAFE_componentWillMount`
    UNSAFE_componentWillMount() {
        // Remember the activeElement from before the modal was displayed
        // that way we can revert the focus back to it after it closes
        this.setState({
            // @ts-expect-error - TS2322 - Type 'Element | null' is not assignable to type 'HTMLElement | null | undefined'.
            activeElement: document.activeElement,
        });
    }

    componentDidMount() {
        if (this.shouldRestrictTabbing()) {
            this._restrictedFocusHandler = (e: Event) =>
                this._restrictedFocus(e);
            // restrict tabbing focus to the modal
            document.addEventListener(
                "focus",
                this._restrictedFocusHandler,
                true,
            );
        }

        if (this.props.keyboard) {
            // @ts-expect-error - TS2769 - No overload matches this call.
            window.addEventListener("keydown", this.handleEsc, true);
        }

        this.oldOverflow = document.body?.style.overflow;
        if (
            this.props.preventBodyScroll &&
            this.state.visible &&
            document.body
        ) {
            document.body.style.overflow = "hidden";
        }

        if (this.props.elementToFocus) {
            const element = this.props.elementToFocus();
            // setTimeout is a hack around the fact that the attempt to focus
            // can sometimes occur before (and therefore be overriden by) a
            // shift in focus associated with the event that triggers inital
            // mounting of the component
            // TODO(amy): figure out a better solution
            if (element) {
                // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API
                // eslint-disable-next-line no-restricted-syntax
                setTimeout(() => element.focus(), 100);
            }
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (
            this.state.visible !== prevState.visible &&
            this.props.preventBodyScroll &&
            document.body
        ) {
            if (this.state.visible) {
                document.body.style.overflow = "hidden";
            } else if (this.oldOverflow != null) {
                document.body.style.overflow = this.oldOverflow;
            }
        }
    }

    componentWillUnmount() {
        if (this.props.keyboard) {
            // @ts-expect-error - TS2769 - No overload matches this call.
            window.removeEventListener("keydown", this.handleEsc, true);
        }

        if (
            this.props.preventBodyScroll &&
            document.body &&
            this.oldOverflow != null
        ) {
            document.body.style.overflow = this.oldOverflow;
        }

        // Revert the focus back to the element that caused the modal to
        // open in the first place (better for accessibility)
        if (this.state.activeElement && this.props.refocusActiveElement) {
            this.state.activeElement.focus();
        }

        // Remove our focus handler used to restrict tabbing
        if (this.shouldRestrictTabbing()) {
            document.removeEventListener(
                "focus",
                this._restrictedFocusHandler,
                true,
            );
        }
    }

    shouldRestrictTabbing(): boolean {
        return !this.props.inline && this.props.restrictTabbing;
    }

    // @ts-expect-error - TS2564 - Property 'modal' has no initializer and is not definitely assigned in the constructor.
    modal: HTMLElement;
    oldOverflow: string | null | undefined;
    // @ts-expect-error - TS2564 - Property '_restrictedFocusHandler' has no initializer and is not definitely assigned in the constructor.
    _restrictedFocusHandler: (e: Event) => void;

    /*
     * Determine whether the target element is valid for focusing
     */
    _restrictedFocus(e: Event) {
        // if focus has moved to something outside the modal, redirect focus
        if (
            e.target instanceof HTMLElement &&
            this.modal &&
            !this.modal.contains(e.target)
        ) {
            this.modal.focus();
        }
    }

    handleClose() {
        this.setState({
            visible: false,
        });

        this.props.onClose();

        // Revert the focus back to the element that caused the modal to
        // open in the first place (better for accessibility)
        if (this.state.activeElement && this.props.refocusActiveElement) {
            this.state.activeElement.focus();
        }

        // Remove our focus handler used to restrict tabbing
        if (this.props.restrictTabbing) {
            document.removeEventListener(
                "focus",
                this._restrictedFocusHandler,
                true,
            );
        }
    }

    handleEsc: (e: React.KeyboardEvent) => void = (e: React.KeyboardEvent) => {
        if (e.keyCode === 27) {
            this.handleClose();
        }
    };

    render(): React.ReactNode {
        // Don't render the dialog if it isn't visible
        if (!this.state.visible && this.props.shouldHandleClose) {
            return <div />;
        }

        const {showCloseButton, showCloseButtonAbsolute} = this.props;
        const closeButton = (showCloseButton || showCloseButtonAbsolute) && (
            <a
                aria-label={this.props.ariaCloseLabel}
                className={css(
                    styles.close,
                    showCloseButtonAbsolute && styles.closeAbsolute,
                )}
                href="javascript: void 0"
                onClick={() => this.handleClose()}
            >
                <DeprecatedIcon icon={closeIcon} size={14} />
            </a>
        );

        let contents = this.props.children;

        if (this.props.title || this.props.footer) {
            contents = (
                <div>
                    {this.props.title && (
                        <div className={css(styles.header)}>
                            <h2 id="modalTitle" className={css(styles.heading)}>
                                {this.props.title}
                            </h2>
                        </div>
                    )}
                    <div className={css(styles.body)}>
                        {this.props.children}
                    </div>
                    {this.props.footer && (
                        <div className={css(styles.footer)}>
                            {this.props.footer}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div
                className={css(
                    styles.wrap,
                    this.props.inline && styles.inlineWrap,
                )}
            >
                <div
                    tabIndex={-1}
                    className={classNames(
                        css(
                            styles.modal,
                            this.props.padded && styles.padded,
                            this.props.forceTop && styles.forceTop,
                            this.props.wide && styles.wide,
                            this.props.extraWide && styles.extraWide,
                            this.props.fullScreen && styles.fullScreen,
                            this.props.fullScreenOnMobile &&
                                styles.fullScreenOnMobile,
                            this.props.inline && styles.inlineModal,
                            this.props.responsive && styles.responsiveModal,
                            this.props.responsive &&
                                this.props.responsiveHeight &&
                                styles.responsiveHeight,
                            ...this.props.extraStyles,
                        ),
                        this.props.className,
                    )}
                    ref={(elem) => {
                        if (elem && this.props.preventScrollOnShow) {
                            elem.style.top = `${window.scrollY}px`;
                        }
                        // @ts-expect-error - TS2322 - Type 'HTMLDivElement | null' is not assignable to type 'HTMLElement'.
                        this.modal = elem;
                    }}
                    role={this.props.alert ? "alertdialog" : "dialog"}
                    aria-labelledby="modalTitle"
                >
                    {closeButton}
                    {contents}
                </div>
                {!!this.props.backdrop && (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error
                    <div
                        className={css(
                            styles.backdrop,
                            this.props.inline && styles.inlineBackdrop,
                            this.props.forceTop && styles.forceTopBackdrop,
                        )}
                        onClick={
                            this.props.backdrop !== "static"
                                ? () => this.handleClose()
                                : undefined
                        }
                    />
                )}
            </div>
        );
    }
}

const defaultWidth = 560;
const wideWidth = 700;
const extraWideWidth = 1000;
const margin = 30;

const styles = StyleSheet.create({
    wrap: {
        display: "inline-block",
    },

    inlineWrap: {
        display: "block",
        overflow: "auto",
        position: "relative",
    },

    modal: {
        ...DeprecatedModal.width(defaultWidth),
        backgroundColor: color.white,
        backgroundClip: "padding-box",
        border: "1px solid rgba(0, 0, 0, 0.3)",
        borderRadius: borderRadiusLarge,
        boxShadow: "0 3px 7px rgba(0, 0, 0, 0.3)",
        color: color.offBlack,
        left: "50%",
        outline: "none",
        position: "fixed",
        top: "10%",
        zIndex: zindexModal,
    },

    padded: {
        padding: modalDialogPadding,
    },

    inlineModal: {
        marginBottom: 10,
        marginTop: 10,
        position: "relative",
    },

    responsiveModal: {
        overflow: "auto",
        width: "75%",
        marginLeft: 0,
        transform: "translate(-50%, 0)",
    },

    responsiveHeight: {
        bottom: "10%",
    },

    forceTop: {
        // forceTop and forceTopBackdrop are indended to have a z-index larger
        // than any other element on the site. The Manticore jipt dialog has a
        // z-index of 2137483648, and the max possible z-index is 2147483647
        zIndex: 2147000001,
    },

    wide: DeprecatedModal.width(wideWidth),

    extraWide: DeprecatedModal.width(extraWideWidth),

    fullScreen: {
        bottom: margin,
        left: margin,
        minHeight: 500 - 2 * margin,
        minWidth: parseFloat(minContainerWidth) - 2 * margin,
        position: "fixed",
        right: margin,
        top: margin,
        width: "auto",
    },

    fullScreenOnMobile: {
        [mediaQueries.smOrSmaller]: {
            border: "none",
            borderRadius: 0,
            boxSizing: "border-box",
            height: "100%",
            left: 0,
            margin: 0,
            maxWidth: "none",
            position: "fixed",
            top: 0,
            width: "100%",
        },
    },

    backdrop: {
        backgroundColor: color.offBlack,
        bottom: 0,
        left: 0,
        opacity: 0.72,
        position: "fixed",
        right: 0,
        top: 0,
        zIndex: zindexModalBackdrop,
    },

    forceTopBackdrop: {
        zIndex: 2147000000,
    },

    inlineBackdrop: {
        position: "absolute",
    },

    close: {
        color: color.offBlack,
        cursor: "pointer",
        float: "right",
        fontSize: baseLineHeight,
        fontWeight: "bold",
        lineHeight: baseLineHeight,
        opacity: 0.2,
        textShadow: `0 1px 0 ${color.white}`,
        textDecoration: "none",

        ":hover": {
            opacity: 0.41,
        },

        ":focus": {
            opacity: 0.41,
        },
    },

    closeAbsolute: {
        position: "absolute",
        fontSize: 28,
        lineHeight: "28px",
        right: 0,
        top: 0,
        // Make it a bigger hit target.  Padding on the top/bottom isn't
        // needed because the containing <a> tag is taller than the icon.
        paddingLeft: 8,
        paddingRight: 8,
        zIndex: zindexAboveModal,
    },

    header: {
        borderBottom: `1px solid ${color.offBlack16}`,
        padding: `0 0 ${modalSectionPadding} 0`,
    },

    heading: {
        color: color.offBlack,
        fontFamily: "inherit",
        fontWeight: "bold",
        marginBottom: 0,
        paddingTop: modalSectionPadding,
    },

    body: {
        margin: 0,
        maxHeight: "none",
        padding: `${contentPadding} 0 ` + `${modalSectionPadding} 0`,
        position: "relative",
    },

    footer: {
        backgroundColor: color.white,
        borderTop: `1px solid ${color.offBlack16}`,
        marginBottom: 0,
        padding: `${modalDialogPadding} 0 0 0`,
        overflow: "hidden",
        textAlign: "right",
    },
});

export default DeprecatedModal;
